import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { FieldEventContext, FieldEventFormApi, SectionKey } from '../lease-details.types';

/** Field-event form API: stable across renders, never subscribes to form state. */
export const useFieldEventFormApi = (): FieldEventFormApi => {
  const { setValue, resetField, getValues } = useFormContext();
  return useMemo(
    () => ({
      setValue: (name, value, options) =>
        setValue(name, value, { shouldDirty: true, shouldValidate: options?.validate ?? false }),
      resetField: (name) => resetField(name),
      getValues: (name) => (name == null ? getValues() : getValues(name)),
    }),
    [setValue, resetField, getValues],
  );
};

export const useFieldEventContextBuilder = <TValue>(
  path: string,
  sectionKey: SectionKey,
  arrayIndex?: number,
) => {
  const { getFieldState } = useFormContext();
  const form = useFieldEventFormApi();
  // `getFieldState` without a `formState` argument reads the live state without subscribing.
  return (value: TValue, optionId?: string): FieldEventContext<TValue> => ({
    value,
    valid: !getFieldState(path).invalid,
    fieldPath: path,
    arrayIndex,
    sectionKey,
    optionId,
    getValues: form.getValues,
    form,
  });
};
